import { useEffect, useState, useCallback } from "react";
import { api, money, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, Truck, Bitcoin, Coins, Send, AtSign, Mail, MessageSquare, Download } from "lucide-react";

const STATUS_META = {
  awaiting_payment: { label: "Awaiting", cls: "bg-amber-400/10 text-amber-300 border-amber-400/40" },
  paid: { label: "Paid", cls: "bg-electric/15 text-electric border-electric/50" },
  shipped: { label: "Shipped", cls: "bg-emerald-400/10 text-emerald-300 border-emerald-400/40" },
  cancelled: { label: "Cancelled", cls: "bg-red-400/10 text-red-300 border-red-400/40" },
};
const STATUSES = ["awaiting_payment", "paid", "shipped", "cancelled"];
const FILTERS = ["all", ...STATUSES];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return String(iso || "").slice(0, 10);
  }
};

const payIcon = (m) => (m === "bitcoin" ? Bitcoin : m === "crypto" ? Coins : m === "venmo" ? AtSign : Send);
const payHandle = (o) => {
  const p = o.payment || {};
  if (o.payment_method === "bitcoin") return p.btc_address;
  if (o.payment_method === "crypto") return (p.coins || ["Solana", "Bitcoin", "XRP"]).join(" · ");
  if (o.payment_method === "venmo") return p.venmo_handle;
  return p.zelle_phone;
};

const CSV_COLUMNS = [
  ["Order #", (o) => o.order_number],
  ["Date", (o) => (o.created_at ? new Date(o.created_at).toISOString() : "")],
  ["Customer", (o) => `${o.shipping?.first_name || ""} ${o.shipping?.last_name || ""}`.trim()],
  ["Email", (o) => o.email || ""],
  ["Payment Method", (o) => o.payment_method || ""],
  ["Status", (o) => o.status || ""],
  ["Subtotal", (o) => (o.subtotal ?? "")],
  ["Discount Code", (o) => o.discount_code || ""],
  ["Discount Amount", (o) => (o.discount_amount ?? "")],
  ["Shipping Label", (o) => o.shipping_label || ""],
  ["Shipping Cost", (o) => (o.shipping_cost ?? "")],
  ["Total", (o) => (o.total ?? "")],
  ["Tracking #", (o) => o.tracking || ""],
  ["Street", (o) => o.shipping?.street || ""],
  ["Apt/Unit", (o) => o.shipping?.apt || ""],
  ["City", (o) => o.shipping?.city || ""],
  ["State/Province", (o) => o.shipping?.state || ""],
  ["Postal Code", (o) => o.shipping?.zip || ""],
  ["Country", (o) => o.shipping?.country || ""],
];

const escapeCsv = (v) => {
  const s = v == null ? "" : String(v);
  return `"${s.replace(/"/g, '""')}"`;
};

const buildCsv = (rows) => {
  const header = CSV_COLUMNS.map((c) => escapeCsv(c[0])).join(",");
  const lines = rows.map((o) => CSV_COLUMNS.map((c) => escapeCsv(c[1](o))).join(","));
  return [header, ...lines].join("\r\n");
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [savingId, setSavingId] = useState("");
  const [tracking, setTracking] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [customMsg, setCustomMsg] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/orders${filter !== "all" ? `?status=${filter}` : ""}`),
      api.get("/admin/stats"),
    ])
      .then(([o, s]) => {
        setOrders(o.data);
        setStats(s.data);
      })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (order, status) => {
    setSavingId(order.order_number);
    try {
      const { data } = await api.put(`/admin/orders/${order.order_number}/status`, { status });
      setOrders((prev) => prev.map((o) => (o.order_number === order.order_number ? data : o)));
      if (selected?.order_number === order.order_number) setSelected(data);
      toast.success(`Order ${order.order_number} → ${STATUS_META[status].label}`);
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId("");
    }
  };

  const saveTracking = async () => {
    if (!selected) return;
    setSavingId(selected.order_number);
    try {
      const { data } = await api.put(`/admin/orders/${selected.order_number}/status`, {
        status: selected.status,
        tracking,
      });
      setOrders((prev) => prev.map((o) => (o.order_number === data.order_number ? data : o)));
      setSelected(data);
      toast.success("Tracking saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId("");
    }
  };

  const sendTrackingEmail = async () => {
    if (!selected) return;
    if (!tracking.trim()) { toast.error("Enter a tracking number first"); return; }
    if (!selected.email) { toast.error("No customer email on this order"); return; }
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/admin/orders/${selected.order_number}/email/tracking`, {
        tracking,
        mark_shipped: true,
      });
      setOrders((prev) => prev.map((o) => (o.order_number === data.order.order_number ? data.order : o)));
      setSelected(data.order);
      toast[data.sent ? "success" : "error"](
        data.sent ? `Tracking email sent to ${data.order.email}` : "Saved, but the email could not be sent"
      );
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSendingEmail(false);
    }
  };

  const sendCustomEmail = async () => {
    if (!selected) return;
    if (!customMsg.trim()) { toast.error("Write a message first"); return; }
    if (!selected.email) { toast.error("No customer email on this order"); return; }
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/admin/orders/${selected.order_number}/email/custom`, {
        subject: customSubject,
        message: customMsg,
      });
      toast[data.sent ? "success" : "error"](
        data.sent ? `Message sent to ${selected.email}` : "The email could not be sent"
      );
      if (data.sent) { setCustomMsg(""); setCustomSubject(""); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSendingEmail(false);
    }
  };

  const openDetails = (o) => {
    setSelected(o);
    setTracking(o.tracking || "");
    setCustomSubject("");
    setCustomMsg("");
  };

  const exportCsv = async (all = false) => {
    setExporting(true);
    try {
      let rows = orders;
      if (all) {
        const { data } = await api.get("/admin/orders");
        rows = data;
      }
      if (!rows || rows.length === 0) {
        toast.error("No orders to export");
        return;
      }
      const stamp = new Date().toISOString().slice(0, 10);
      const scope = all ? "all" : filter;
      downloadCsv(`century-orders-${scope}-${stamp}.csv`, buildCsv(rows));
      toast.success(`Exported ${rows.length} order${rows.length === 1 ? "" : "s"} to CSV`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  const statCards = stats
    ? [
        { label: "Orders", value: stats.orders_total },
        { label: "Revenue collected", value: money(stats.revenue_collected) },
        { label: "Awaiting payment", value: stats.by_status?.awaiting_payment ?? 0 },
        { label: "Active subs", value: stats.active_subscriptions ?? 0 },
      ]
    : [];

  return (
    <div data-testid="admin-orders">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="admin-order-stats">
          {statCards.map((s) => (
            <div key={s.label} className="border border-white/12 bg-[#0B1024] p-4 sq-lg">
              <div className="font-display font-bold text-2xl text-white">{s.value}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters + export */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap gap-2" data-testid="admin-order-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold border transition-colors duration-200 sq-md sq-press ${
                filter === f ? "bg-electric text-black border-electric" : "bg-transparent text-white border-white/25 hover:border-electric hover:text-electric"
              }`}
              data-testid={`order-filter-${f}`}
            >
              {f === "all" ? "All" : STATUS_META[f].label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => exportCsv(false)}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold border border-white/25 text-white hover:border-electric hover:text-electric transition-colors duration-200 sq-md sq-press disabled:opacity-50"
            data-testid="admin-orders-export-current"
            title="Export the orders currently shown (respects the status filter)"
          >
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            Export CSV
          </button>
          <button
            onClick={() => exportCsv(true)}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold border border-electric/50 text-electric hover:bg-electric hover:text-black transition-colors duration-200 sq-md sq-press disabled:opacity-50"
            data-testid="admin-orders-export-all"
            title="Export all orders regardless of the current filter"
          >
            <Download size={13} />
            Export All
          </button>
        </div>
      </div>

      {loading ? (
        <p className="py-20 text-center text-sm tracking-widest uppercase text-white/60">Loading...</p>
      ) : orders.length === 0 ? (
        <p className="py-20 text-center text-sm tracking-[0.2em] uppercase text-white/40" data-testid="admin-orders-empty">No orders found</p>
      ) : (
        <div className="border border-white/15 sq-lg overflow-hidden" data-testid="admin-orders-table">
          <div className="hidden md:grid grid-cols-[160px_100px_1fr_110px_110px_130px] border-b border-white/15 bg-electric text-black text-[10px] tracking-[0.2em] uppercase font-bold">
            <div className="p-3">Order</div>
            <div className="p-3">Date</div>
            <div className="p-3">Customer</div>
            <div className="p-3">Total</div>
            <div className="p-3">Pay</div>
            <div className="p-3">Status</div>
          </div>
          {orders.map((o) => {
            const meta = STATUS_META[o.status] || STATUS_META.awaiting_payment;
            const PayI = payIcon(o.payment_method);
            return (
              <div
                key={o.order_number}
                className="grid grid-cols-2 md:grid-cols-[160px_100px_1fr_110px_110px_130px] items-center border-b border-white/12 last:border-b-0 hover:bg-white/[0.02]"
                data-testid={`admin-order-row-${o.order_number}`}
              >
                <button onClick={() => openDetails(o)} className="p-3 text-left" data-testid={`order-view-${o.order_number}`}>
                  <span className="font-display font-bold text-xs text-electric hover:underline">{o.order_number}</span>
                </button>
                <div className="p-3 text-xs text-white/60">{fmtDate(o.created_at)}</div>
                <div className="p-3 min-w-0">
                  <div className="text-sm font-bold text-white truncate">{o.shipping?.first_name} {o.shipping?.last_name}</div>
                  <div className="text-[10px] text-white/40 truncate">{o.email}</div>
                </div>
                <div className="p-3 font-display font-bold text-white text-sm">{money(o.total)}</div>
                <div className="p-3">
                  <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase text-white/70">
                    <PayI size={13} className="text-electric" /> {o.payment_method}
                  </span>
                </div>
                <div className="p-3 flex items-center gap-2">
                  {savingId === o.order_number ? (
                    <Loader2 size={14} className="animate-spin text-electric" />
                  ) : (
                    <select
                      value={o.status}
                      onChange={(e) => changeStatus(o, e.target.value)}
                      className={`text-[10px] tracking-[0.1em] uppercase font-bold border px-2 py-1.5 bg-transparent outline-none cursor-pointer sq-sm ${meta.cls}`}
                      data-testid={`order-status-${o.order_number}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="bg-[#0B1024] text-white">{STATUS_META[s].label}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Details modal */}
      {selected && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4" onClick={() => setSelected(null)}>
          <div className="bg-[#0B1024] border border-white/15 w-full max-w-lg my-auto sq-xl" onClick={(e) => e.stopPropagation()} data-testid="admin-order-detail">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/15">
              <div>
                <h2 className="font-display font-bold text-lg tracking-tighter uppercase text-white">{selected.order_number}</h2>
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-0.5">{fmtDate(selected.created_at)}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-white/70 hover:text-electric" data-testid="admin-order-detail-close"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              {/* Items */}
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-electric mb-3">Items</div>
                <div className="space-y-2">
                  {(selected.items || []).map((it, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-white/70">{it.name} × {it.quantity}</span>
                      <span className="text-white font-bold">{money(it.price * it.quantity)}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Totals */}
              <div className="border-t border-white/12 pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-white/60"><span>Subtotal</span><span className="text-white">{money(selected.subtotal)}</span></div>
                {selected.discount_amount > 0 && (
                  <div className="flex justify-between text-electric-400"><span>Discount ({selected.discount_code})</span><span>minus {money(selected.discount_amount)}</span></div>
                )}
                <div className="flex justify-between text-white/60"><span>Shipping ({selected.shipping_label})</span><span className="text-white">{selected.shipping_cost ? money(selected.shipping_cost) : "Free"}</span></div>
                <div className="flex justify-between pt-2 border-t border-white/12"><span className="font-bold text-white uppercase text-xs tracking-[0.15em]">Total</span><span className="font-display font-bold text-lg text-white">{money(selected.total)}</span></div>
              </div>
              {/* Payment */}
              <div className="border border-white/12 bg-[#0C1128] p-4 sq-md">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-electric mb-2">Payment · {selected.payment_method}</div>
                <div className="text-sm text-white break-all" data-testid="admin-order-pay-handle">{payHandle(selected) || ", "}</div>
                {selected.payment_method === "bitcoin" && selected.payment?.btc_amount != null && (
                  <div className="text-xs text-white/50 mt-1">{selected.payment.btc_amount} BTC</div>
                )}
              </div>
              {/* Shipping address */}
              <div className="border border-white/12 bg-[#0C1128] p-4 sq-md">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-electric mb-2">Ship to</div>
                <div className="text-sm text-white leading-relaxed">
                  {selected.shipping?.first_name} {selected.shipping?.last_name}<br />
                  {selected.shipping?.street}{selected.shipping?.apt ? `, ${selected.shipping.apt}` : ""}<br />
                  {selected.shipping?.city}, {selected.shipping?.state} {selected.shipping?.zip}<br />
                  {selected.shipping?.country}
                </div>
              </div>
              {/* Tracking + status actions */}
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-2 block">Tracking number</label>
                <div className="flex gap-2">
                  <input
                    value={tracking}
                    onChange={(e) => setTracking(e.target.value)}
                    placeholder="Carrier tracking #"
                    className="cl-input flex-1"
                    data-testid="admin-order-tracking-input"
                  />
                  <button onClick={saveTracking} disabled={savingId === selected.order_number} className="btn-ghost px-5 text-xs tracking-[0.2em] uppercase font-bold disabled:opacity-50" data-testid="admin-order-tracking-save">Save</button>
                </div>
                <button
                  onClick={sendTrackingEmail}
                  disabled={sendingEmail}
                  className="btn-primary w-full mt-2 inline-flex items-center justify-center gap-2 py-3 text-[10px] tracking-[0.2em] uppercase font-bold disabled:opacity-50"
                  data-testid="admin-order-email-tracking"
                >
                  {sendingEmail ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                  Email tracking to customer
                </button>
                <p className="text-[10px] text-white/40 mt-2 leading-relaxed">
                  Sends a branded "your order shipped" email with this tracking number to{" "}
                  <span className="text-white/60">{selected.email || ",  no email on file , "}</span> and marks the order Shipped.
                </p>
              </div>

              {/* Custom message email */}
              <div className="border-t border-white/12 pt-4">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-electric mb-3 inline-flex items-center gap-2">
                  <MessageSquare size={13} /> Send a custom message
                </div>
                <input
                  value={customSubject}
                  onChange={(e) => setCustomSubject(e.target.value)}
                  placeholder="Subject (optional)"
                  className="cl-input w-full mb-2"
                  data-testid="admin-order-custom-subject"
                />
                <textarea
                  value={customMsg}
                  onChange={(e) => setCustomMsg(e.target.value)}
                  placeholder="Write a message to the customer..."
                  rows={4}
                  className="cl-input w-full resize-none"
                  data-testid="admin-order-custom-message"
                />
                <button
                  onClick={sendCustomEmail}
                  disabled={sendingEmail}
                  className="btn-ghost w-full mt-2 inline-flex items-center justify-center gap-2 py-3 text-[10px] tracking-[0.2em] uppercase font-bold disabled:opacity-50"
                  data-testid="admin-order-email-custom"
                >
                  {sendingEmail ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                  Send message
                </button>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={() => changeStatus(selected, "paid")} className="btn-ghost px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase font-bold" data-testid="admin-order-mark-paid">Mark Paid</button>
                <button onClick={() => changeStatus(selected, "shipped")} className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase font-bold" data-testid="admin-order-mark-shipped"><Truck size={13} /> Mark Shipped</button>
                <button onClick={() => changeStatus(selected, "cancelled")} className="px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase font-bold border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-colors duration-200 sq-md sq-press" data-testid="admin-order-cancel">Cancel Order</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
